import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import AppsIcon from '@mui/icons-material/Apps';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, postRemoteScreenWebrtcAddress, postStopWebrtcByMac, RemoteControlCommand } from '../services/api';
import FastKeySocket from '../utils/fastKeySocket';

function parseStreamUrl(streamUrl: string) {
  const trimmed = streamUrl.replace(/^webrtc:\/\//i, '');
  const [hostAndPort, ...pathParts] = trimmed.split('/');
  const host = hostAndPort.trim();
  const path = `/${pathParts.join('/')}`;
  const apiBase = host ? `https://${host}/rtc/v1/play/` : '';
  return { apiBase, streamUrl, path };
}

async function waitForIceGatheringComplete(pc: RTCPeerConnection) {
  if (pc.iceGatheringState === 'complete') return;
  await new Promise<void>((resolve) => {
    const timeout = setTimeout(() => resolve(), 2000);
    const handler = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', handler);
        clearTimeout(timeout);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', handler);
  });
}

export default function RemoteScreenViewer() {
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const pcRef = React.useRef<RTCPeerConnection | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState('Connecting...');
  const [remoteError, setRemoteError] = React.useState<string | null>(null);
  const [stopError, setStopError] = React.useState<string | null>(null);
  const [stopLoading, setStopLoading] = React.useState(false);
  const [restartLoading, setRestartLoading] = React.useState(false);
  
  const fastSockRef = React.useRef<FastKeySocket | null>(null);

  const initialStreamUrl = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('streamUrl') ?? '';
  }, [location.search]);

  const [streamUrl, setStreamUrl] = React.useState(initialStreamUrl);

  const sn = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('sn') ?? '';
  }, [location.search]);

  const mac = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('mac') ?? '';
  }, [location.search]);

  const renderCommandIcon = React.useCallback(
    (_command: RemoteControlCommand, icon: React.ReactNode) => icon,
    []
  );

  const handleRemoteCommand = React.useCallback(
    (command: RemoteControlCommand) => {
      if (!sn) {
        return;
      }
      // If fast socket is connected, send keycode frame over WS using MAC and command mapping
      const sock = fastSockRef.current;
      const keyParam = Number(command);
      // prefer mac if available
      const targetMac = mac ?? '';
      if (!sock) {
        setRemoteError('Fast socket not connected');
        return;
      }
      const sent = sock.sendKey(targetMac, keyParam);
      if (!sent) {
        setRemoteError('Failed to send command over websocket');
      }
    },
    [sn, mac]
  );

  // Manage FastKeySocket lifecycle when user toggles useFastSocket
  // Auto-connect FastKeySocket when mac is present — WebSocket is the default transport now
  React.useEffect(() => {
    // Build the observed fast-UI websocket handshake URL. The fast UI you inspected
    // opened a connection like:
    //   wss://manage.atvbridge.com/websocket?mac=98:C9:BE:1E:8E:6D&action=cmd_key_code
    // We attempt the same pattern here. Note: the cookie used for auth (if any) must
    // already be present for the browser to include it in the WS handshake.
    if (!mac) {
      return;
    }

    const encodedMac = encodeURIComponent(mac);
    const WS_URL = `wss://manage.atvbridge.com/websocket?mac=${encodedMac}&action=cmd_key_code`;
    const sock = new FastKeySocket();
    fastSockRef.current = sock;
    sock.onopen = () => {
      // Keep only a single console.info when socket opens so we have minimal logging
      console.info('FastKeySocket open');
    };
    sock.onclose = () => { /* intentionally silent */ };
    sock.onerror = () => { /* intentionally silent */ };
    sock.onmessage = () => { /* intentionally silent */ };

    sock.connect({ url: WS_URL, autoReconnect: true });

    return () => {
      try { sock.close(); } catch (e) { /* ignore */ }
      if (fastSockRef.current === sock) fastSockRef.current = null;
    };
  }, [mac]);

  const handleStopWebrtc = React.useCallback(() => {
    if (!mac) {
      setStopError('Device MAC missing for stop request.');
      return;
    }
    setStopLoading(true);
    void postStopWebrtcByMac({ mac })
      .catch((err: any) => {
        setStopError(err?.message ?? 'Failed to stop WebRTC stream.');
      })
      .finally(() => {
        setStopLoading(false);
        navigate(-1);
      });
  }, [mac, navigate]);

  const handleRestartWebrtc = React.useCallback(() => {
    if (!mac || !sn) {
      setStopError('Device SN or MAC missing for restart request.');
      return;
    }
    setRestartLoading(true);
    void postStopWebrtcByMac({ mac })
      .catch((err: any) => {
        setStopError(err?.message ?? 'Failed to stop WebRTC stream.');
      })
      .then(() => postRemoteScreenWebrtcAddress({ sn }))
      .then((resp) => {
        const code = resp?.code;
        const ok = code === 0 || code === 200;
        if (!ok || !resp?.data) {
          throw new Error(resp?.message ?? 'Failed to fetch new WebRTC address.');
        }
        setStreamUrl(resp.data);
      })
      .catch((err: any) => {
        setStopError(err?.message ?? 'Failed to restart WebRTC stream.');
        setLoading(false);
      })
      .finally(() => {
        setRestartLoading(false);
      });
  }, [mac, sn]);

  React.useEffect(() => {
    let cancelled = false;
    const start = async () => {
      if (!streamUrl) {
        setError('Missing stream URL.');
        setLoading(false);
        return;
      }
      const { apiBase } = parseStreamUrl(streamUrl);
      if (!apiBase) {
        setError('Invalid stream URL.');
        setLoading(false);
        return;
      }

  pcRef.current?.close();
  const pc = new RTCPeerConnection();
  pcRef.current = pc;

      pc.addEventListener('track', (event) => {
        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
        }
      });

      pc.addTransceiver('audio', { direction: 'recvonly' });
      pc.addTransceiver('video', { direction: 'recvonly' });

      try {
        setStatus('Creating offer...');
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await waitForIceGatheringComplete(pc);

        const payload = {
          streamurl: streamUrl,
          sdp: pc.localDescription?.sdp,
        };

        setStatus('Requesting stream...');
        const response = await axios.post(`${API_BASE_URL}/api/remote-screen/play`, payload);  // Wrapper around https://manage.atvbridge.com/rtc/v1/play/
        const answerSdp = response?.data?.sdp;
        if (!answerSdp) {
          throw new Error('Missing SDP answer from server.');
        }
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

        if (!cancelled) {
          setStatus('Streaming');
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message ?? 'Failed to start stream.');
          setLoading(false);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      pcRef.current?.close();
      pcRef.current = null;
    };
  }, [streamUrl]);

  // Listen for postMessage from the fast popup and capture its window reference.
  // The fast UI posts messages with { from: 'remote-screen', ... } — use that to cache event.source
  React.useEffect(() => {
    const handler = (ev: MessageEvent) => {
      try {
        if (typeof ev.data !== 'string') return;
        const parsed = JSON.parse(ev.data);
        if (parsed && parsed.from === 'remote-screen') {
          // store the popup window proxy so we can postMessage back later
          try {
            (window as any)['__remoteScreenSdk'] = ev.source;
          } catch (err) {
            // can't directly set if cross-origin restrictions apply, but event.source is usable with postMessage
            (window as any)['__remoteScreenSdk'] = ev.source;
          }
        }
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: '#000',
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="contained" color="error" onClick={handleStopWebrtc} size="small" disabled={!mac || stopLoading || restartLoading}>
          Stop
        </Button>
        <Button variant="outlined" onClick={handleRestartWebrtc} size="small" disabled={!mac || !sn || stopLoading || restartLoading}>
          Restart
        </Button>
        <Typography variant="body2" color="rgba(255,255,255,0.8)">{status}</Typography>
      </Box>

      <Box sx={{ position: 'absolute', right: 24, bottom: 24, zIndex: 3 }}>
        <Paper
          elevation={8}
          sx={{
            p: 2,
            width: 260,
            bgcolor: 'linear-gradient(180deg, rgba(28,28,30,0.95), rgba(12,12,14,0.9))',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(8px)',
            borderRadius: '6px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
            overflow: 'hidden',
          }}
        >
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="img" src="/ATV Bridge Logo.png" alt="Translite Global" sx={{ height: 20, width: 'auto', display: 'block' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                  Remote Control
                </Typography>
              </Box>

              <Typography variant="caption" color="rgba(255,255,255,0.72)">
                {sn ? `Sn: ${sn}` : ''}
                <br />
                {mac ? `Mac: ${mac}` : ''}
              </Typography>
              
              {/* Power button on its own centered row */}
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 0.5 }}>
                  <IconButton
                    onClick={() => handleRemoteCommand(RemoteControlCommand.POWER)}
                    disabled={!sn}
                    sx={{
                      position: 'relative',
                      bgcolor: 'linear-gradient(180deg,#ff3b30,#e81e1e)',
                      color: '#fff',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      boxShadow: '0 10px 26px rgba(232,30,30,0.18)',
                      outline: '2px solid rgba(255,255,255,0.12)',
                      outlineOffset: '4px',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -12,
                        top: -12,
                        right: -12,
                        bottom: -12,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle at center, rgba(255,77,77,0.14) 0%, rgba(255,77,77,0.06) 90%, transparent 10%)',
                        filter: 'blur(6px)',
                        zIndex: 0,
                      },
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 14px 32px rgba(232,30,30,0.2)' },
                      '& > svg': { position: 'relative', zIndex: 1, color: '#fff' },
                    }}
                  >
                    <PowerSettingsNewIcon sx={{ fontSize: 30 }} />
                  </IconButton>
              </Box>

            </Box>

            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button
                size="small"
                variant="contained"
                onClick={() => handleRemoteCommand(RemoteControlCommand.HOME)}
                disabled={!sn}
                startIcon={renderCommandIcon(RemoteControlCommand.HOME, <HomeIcon fontSize="small" />)}
                sx={{
                  color: '#111',
                  background: 'linear-gradient(180deg,#fff,#f0f0f0)',
                  borderRadius: 1,
                  flex: 1,
                  height: 40,
                  minWidth: 84,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                  '&:hover': { transform: 'translateY(-1px)' },
                }}
              >
                Home
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleRemoteCommand(RemoteControlCommand.RETURN)}
                disabled={!sn}
                startIcon={renderCommandIcon(RemoteControlCommand.RETURN, <KeyboardReturnIcon fontSize="small" />)}
                sx={{
                  color: '#111',
                  background: 'linear-gradient(180deg,#fff,#f0f0f0)',
                  borderRadius: 1,
                  flex: 1,
                  height: 40,
                  minWidth: 84,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                  '&:hover': { transform: 'translateY(-1px)' },
                }}
              >
                Back
              </Button>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 56px)',
                gridTemplateRows: 'repeat(3, 56px)',
                gap: 1,
                justifyContent: 'center',
              }}
            >
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.UP)}
                disabled={!sn}
                sx={{ gridColumn: 2, gridRow: 1, color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {renderCommandIcon(RemoteControlCommand.UP, <KeyboardArrowUpIcon />)}
              </IconButton>
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.LEFT)}
                disabled={!sn}
                sx={{ gridColumn: 1, gridRow: 2, color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {renderCommandIcon(RemoteControlCommand.LEFT, <KeyboardArrowLeftIcon />)}
              </IconButton>
              <Button
                variant="contained"
                onClick={() => handleRemoteCommand(RemoteControlCommand.OK)}
                disabled={!sn}
                sx={{ gridColumn: 2, gridRow: 2, minWidth: 0, width: 56, height: 56, fontWeight: 700 }}
              >
                OK
              </Button>
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.RIGHT)}
                disabled={!sn}
                sx={{ gridColumn: 3, gridRow: 2, color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {renderCommandIcon(RemoteControlCommand.RIGHT, <KeyboardArrowRightIcon />)}
              </IconButton>
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.DOWN)}
                disabled={!sn}
                sx={{ gridColumn: 2, gridRow: 3, color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {renderCommandIcon(RemoteControlCommand.DOWN, <KeyboardArrowDownIcon />)}
              </IconButton>
            </Box>

            {/* Apps button row (centered) */}
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 0.5 }}>
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.APPS)}
                disabled={!sn}
                sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.25)', width: 48, height: 48 }}
              >
                {renderCommandIcon(RemoteControlCommand.APPS, <AppsIcon />)}
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="space-between">
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.VOLUME_DOWN)}
                disabled={!sn}
                sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.25)', flex: 1 }}
              >
                {renderCommandIcon(RemoteControlCommand.VOLUME_DOWN, <VolumeDownIcon />)}
              </IconButton>
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.MUTE)}
                disabled={!sn}
                sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.25)', flex: 1 }}
              >
                {renderCommandIcon(RemoteControlCommand.MUTE, <VolumeOffIcon />)}
              </IconButton>
              <IconButton
                onClick={() => handleRemoteCommand(RemoteControlCommand.VOLUME_UP)}
                disabled={!sn}
                sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.25)', flex: 1 }}
              >
                {renderCommandIcon(RemoteControlCommand.VOLUME_UP, <VolumeUpIcon />)}
              </IconButton>
            </Stack>
           
            
          </Stack>
        </Paper>
      </Box>

      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
          <CircularProgress color="inherit" />
        </Box>
      )}

      {(stopLoading || restartLoading) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 4,
            bgcolor: 'rgba(0,0,0,0.6)',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body2" color="rgba(255,255,255,0.85)">
            {stopLoading ? 'Stopping remote screen...' : 'Restarting remote screen...'}
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ position: 'absolute', top: 64, left: 16, right: 16, zIndex: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
      />

      <Snackbar
        open={Boolean(remoteError)}
        autoHideDuration={3000}
        onClose={() => setRemoteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {remoteError ? <Alert severity="error">{remoteError}</Alert> : undefined}
      </Snackbar>

      {/* fastMessage UI removed — WebSocket opens silently (console.info on open only) */}

      <Snackbar
        open={Boolean(stopError)}
        autoHideDuration={3000}
        onClose={() => setStopError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {stopError ? <Alert severity="error">{stopError}</Alert> : undefined}
      </Snackbar>

    </Box>
  );
}
